// React and third-party libraries
import * as React from "react";

// Local imports (styles, constants, assets, etc.)
import { getImage } from "gatsby-plugin-image";
import { graphql } from "gatsby";

// Components imports
import HeroSection from "../components/HeroSection";
import HardwareSection from "../components/HardwareSection";
import SolutionsSection from "../components/SolutionsSection";
import GetInTouchSection from "../../../components/GetInTouchSection";
import Seo from "../../../components/seo";

// Translation
import { useTranslation } from "react-i18next";
import i18next from "i18next";

/**
 * LongRange Component - Landing page for Long Range Tracking technology
 *
 * Displays sections for hero, IoT hardware, solutions, and a contact CTA.
 */
const LongRange = ({ data }) => {
  const { t } = useTranslation(); // Explicitly specify the namespace

  const heroBackgroundImageData = getImage(data?.heroImage) || null;
  const gatewayImageData = getImage(data?.gatewayImg) || null;
  const tagImageData = getImage(data?.tagImg) || null;

  // Hero section content
  const heroContent = {
    title: t("longRangeTracking.mainTitle"),
    subtitle: t("longRangeTracking.subtitle"),
    imageData: heroBackgroundImageData,
  };

  // Hardware section content
  const hardwareContent = {
    title: t("longRangeTracking.hardwareContent.title"),
    subtitle: t("longRangeTracking.hardwareContent.subtitle"),
    hardwareList: [
      {
        title: t("longRangeTracking.hardwareContent.hardwareList.0.title"),
        subtitle: t(
          "longRangeTracking.hardwareContent.hardwareList.0.subtitle"
        ),
        link: "/products/long-range/gateway",
        imageData: gatewayImageData,
        imgAlt: "Gateway",
      },
      {
        title: t("longRangeTracking.hardwareContent.hardwareList.1.title"),
        subtitle: t(
          "longRangeTracking.hardwareContent.hardwareList.1.subtitle"
        ),
        link: "/products/long-range/tag",
        imageData: tagImageData,
        imgAlt: "Tag",
      },
    ],
  };

  // Solutions section content
  const solutionsContent = {
    title: t("longRangeTracking.solutionsContent.title"),
    subtitle: t("longRangeTracking.solutionsContent.subtitle"),
    solutionsList: [
      {
        title: t("longRangeTracking.solutionsContent.solutionsList.0.title"),
        subtitle: t(
          "longRangeTracking.solutionsContent.solutionsList.0.subtitle"
        ),
        link: "/solutions/inventory-management",
        useCases: [
          {
            name: t(
              "longRangeTracking.solutionsContent.solutionsList.0.useCases.0.name"
            ),
            link: "/use-cases/metal-containers-monitoring",
          },
          {
            name: t(
              "longRangeTracking.solutionsContent.solutionsList.0.useCases.1.name"
            ),
            link: "/use-cases/smart-waste-management-system",
          },
          {
            name: t(
              "longRangeTracking.solutionsContent.solutionsList.0.useCases.2.name"
            ),
            link: "/use-cases/reverse-logistics-tracking",
          },
        ],
      },
    ],
  };

  return (
    <>
      <HeroSection {...heroContent} />
      <HardwareSection {...hardwareContent} />
      <SolutionsSection {...solutionsContent} />
      <GetInTouchSection />
    </>
  );
};

export default LongRange;

export const query = graphql`
  query {
    heroImage: file(relativePath: { eq: "long-range-hero-bg.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, layout: FULL_WIDTH, quality: 30)
      }
    }
    tagImg: file(relativePath: { eq: "long-range-tag-front.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 250
          placeholder: BLURRED
          layout: CONSTRAINED
          quality: 100
        )
      }
    }
    gatewayImg: file(relativePath: { eq: "long-range-gateway.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 250
          placeholder: BLURRED
          layout: CONSTRAINED
          quality: 100
        )
      }
    }
  }
`;

export const Head = ({ location }) => {
  const { t } = useTranslation();

  return (
    <Seo
      title={t("longRangeTracking.seo.title")} // Specific title for the index
      description={t("longRangeTracking.seo.description")} // Specific description
      pathname={location.pathname} // Pass the current page's path
      lang={t(i18next.language, { defaultValue: "en" })}
    // imageSrc={...} // Optionally provide a specific image for the index page
    // Otherwise, it will use the default from gatsby-config
    />
  );
};
